import {
  Button,
  Card,
  Input,
  Spinner,
  Text,
  Textarea,
  Dropdown,
  Option,
  makeStyles,
  shorthands,
  tokens,
  Field,
  MessageBarGroup
} from "@fluentui/react-components";
import { UserInfo } from "@microsoft/teamsfx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageInfo } from "../../MessageContext";
import { CreateTickedDto, createTicket } from "../../apis/ZohoApi";
import { ConfirmationDialog } from "../../components/dialogs";
import { useQueryClient } from "react-query";
import logo from "../../images/itp_logo.png";
import { Auth } from "../../utils/auth";
import { useGetTenantName } from "../../apis/GraphApi";
import { PopularArticles } from "../../components/PopularArticles";
import { NotificationBar } from "../../components";
import { StringValidator } from "../../utils/StringValidator";

const useStyles = makeStyles({
  page: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh"
  },
  pageCard: {
    maxWidth: "1000px",
    borderRadius: "10px",
    gap: "0px",
    margin: "20px 0px",
    padding: "0px"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "67px",
    backgroundColor: "#0099DA"
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "Montserrat, sans-serif",
    textShadow: "0px 4px 4px #00000025",
    margin: "0px auto",
  },
  notificationBar: {
    width: "990px",
    maxWidth: "100%",
    textAlign: "center",
    marginTop: "3px",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
  },
  body: {
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    padding: "20px"
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: "2em"
  },
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: "1em",
    marginBottom: "1em"
  },
  disabledInput: {
    backgroundColor: tokens.colorNeutralBackgroundDisabled
  },
  phoneMobileRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1em"
  },
  phoneInput: {
    height: "fit-content",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column"
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    height: "36px",
    gap: "10px",
    justifyContent: "right",
    ...shorthands.margin("1em", 0)
  },
  confirmationDialog: {
    maxWidth: "400px"
  },
  formColumn: {
    display: "flex",
    flexDirection: "column"
  },
  articlesColumn: {
    display: "flex",
    flexDirection: "column"
  },
  articlesCard: {
    marginTop: "1em", 
    maxWidth: "400px"
  }
});

class TicketValidator {
  public errors: CreateTickeValidationErrors = {};
  constructor(private form: CreateTickedDto, private user: UserInfo) {}

  validateAll(): boolean {
    this.validateSecondaryEmail(this.form.secondaryEmail, this.user.preferredUserName);
    this.validateMobile(this.form.mobile);
    this.validatePhone(this.form.mobile);
    this.validateSubject(this.form.subject);
    this.validateDescription(this.form.description);
    return Object.keys(this.errors).length === 0;
  }

  validateSecondaryEmail(value: string, userEmail: string) {
    if (value === userEmail) {
      this.errors.secondaryEmail = "Secondary email cannot be the same as User Email";
    } else if (value && !StringValidator.isValidEmail(value)) {
      this.errors.secondaryEmail = "Please enter a valid email address";
    }
  }

  validatePhone(value: string) {
    this.checkLength(value, "phone", 50);
  }

  validateMobile(value: string) {
    this.checkLength(value, "mobile", 50);
  }

  validateSubject(value: string) {
    if (!value) {
      this.errors.subject = "Ticket subject is required";
    } else {
      this.checkLength(value, "subject", 255);
    }
  }

  validateDescription(value: string) {
    this.checkLength(value, "description", 5000);
  }

  checkLength(value: string, field: string, length: number) {
    if (value && value.length > length) {
      this.errors[field] = `Maximum field length is ${length} characters`;
    }
  }
}

type CreateTickeValidationErrors = {
  phone?: string;
  mobile?: string;
  secondaryEmail?: string;
  subject?: string;
  description?: string;
};

export const CreateTicketPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const styles = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState<MessageInfo>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const [userData, setUserData] = useState<UserInfo>(null);
  const [formData, setFormData] = useState<CreateTickedDto>({
    phone: "",
    mobile: "",
    secondaryEmail: "",
    subject: "",
    description: "",
    priority: "Normal"
  });
  const [errors, setErrors] = useState<CreateTickeValidationErrors>({});
  const { data: companyName } = useGetTenantName();
  const validator = new TicketValidator(formData, userData);

  useEffect(() => {
    Auth.getUserInfo().then(user => {
      setUserData(user);
    });
  }, []);

  const updateField = (field: keyof CreateTickedDto, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  const updateError = (field: keyof CreateTickeValidationErrors, value: string = validator.errors[field]) => {
    setErrors(prev => ({ ...prev, [field]: value }));
  }

  const onCancelClick = () => navigate("/tab");

  const onSubmitClick = () => {
    if (validator.validateAll()) {
      setDialogOpen(true);
    } else {
      setErrors(validator.errors);
    }
  };

  const onConfirmClick = () => {
    setLoading(true);
    setDialogOpen(false);
    createTicket(formData)
      .then(() => {
        setLoading(false);
        queryClient.setQueryData('notificationMessage', {
          intent: "success",
          description: "Your support request has been submitted successfully"
        });
        navigate("/tab");
      })
      .catch((e: Error) => {
        setLoading(false);
        setMessage({
          intent: "error",
          title: "Your request was not submitted. Please try again later.",
          description: `Error message: ${e.message}`
        });
      });
  };

  return (
    <div className={styles.page}>
      <Card className={styles.pageCard}>
        <div className={styles.header}>
          <div style={{ position: "absolute" }}>
            <img src={logo} height="67px" alt="logo" />
          </div>
          <Text className={styles.title} weight="medium">
            Submit a Ticket
          </Text>
        </div>
        <MessageBarGroup>
          {message && <NotificationBar className={styles.notificationBar} onDismiss={() => setMessage(null)} {...message}/>}
        </MessageBarGroup>
        <div className={styles.body}>
          <div className={styles.contentGrid}>
            <div className={styles.formColumn}>
              <div className={styles.formSection}>
                <Field label="Tenant Name">
                  <Input className={styles.disabledInput} disabled value={companyName} />
                </Field>
                <Field label="User Name">
                  <Input className={styles.disabledInput} disabled value={userData?.displayName || ""} />
                </Field>
                <Field label="User Email">
                  <Input className={styles.disabledInput} disabled value={userData?.preferredUserName || ""} />
                </Field>
                <div className={styles.phoneMobileRow}>
                  <Field className={styles.phoneInput}
                    label="Phone"
                    validationMessage={errors.phone}
                    validationState={errors.phone ? "error" : undefined}>
                    <Input type="tel"
                      value={formData.phone}
                      onChange={(_, data) => {
                        validator.validatePhone(data.value);
                        updateField("phone", data.value);
                        updateError("phone");
                      }}
                    />
                  </Field>
                  <Field className={styles.phoneInput}
                    label="Mobile"
                    validationMessage={errors.mobile}
                    validationState={errors.mobile ? "error" : undefined}>
                    <Input type="tel"
                      value={formData.mobile}
                      onChange={(_, data) => {
                        validator.validateMobile(data.value);
                        updateField("mobile", data.value);
                        updateError("mobile");
                      }}
                    />
                  </Field>
                </div>
                <Field label="Secondary Email"
                  validationMessage={errors.secondaryEmail}
                  validationState={errors.secondaryEmail ? "error" : undefined}>
                  <Input type="email"
                    value={formData.secondaryEmail}
                    placeholder="Enter email address"
                    onBlur={() => {
                      validator.validateSecondaryEmail(formData.secondaryEmail, userData?.preferredUserName);
                      if (validator.errors.secondaryEmail)
                        updateError("secondaryEmail");
                    }}
                    onChange={(_, data) => {
                      updateField("secondaryEmail", data.value);
                      updateError("secondaryEmail", "");
                    }}/>
                </Field>
                <Field label="Priority">
                  <Dropdown value={formData.priority} onOptionSelect={(_, data) => updateField("priority", data.optionValue)}>
                    <Option value="Low">Low</Option>
                    <Option value="Normal">Normal</Option>
                    <Option value="High">High</Option>
                    <Option value="Urgent">Urgent</Option>
                  </Dropdown>
                </Field>
                <Field label="Ticket Subject" required validationMessage={errors.subject} validationState={errors.subject ? "error" : undefined}>
                  <Input value={formData.subject}
                    placeholder="Enter your request subject"
                    onChange={(_, data) => {
                      validator.validateSubject(data.value);
                      updateField("subject", data.value);
                      updateError("subject");
                    }} />
                </Field>
                <Field label="Ticket Description" validationMessage={errors.description} validationState={errors.description ? "error" : undefined}>
                  <Textarea
                    appearance="outline"
                    placeholder="Enter your request details"
                    resize="vertical"
                    value={formData.description}
                    onChange={(_, data) => {
                      validator.validateDescription(data.value);
                      updateField("description", data.value);
                      updateError("description");
                    }}
                    rows={8}
                  />
                </Field>
              </div>
              <div className={styles.buttons}>
                <Button onClick={onCancelClick} size="large">
                  Cancel
                </Button>
                <Button
                  disabled={isLoading}
                  icon={isLoading ? <Spinner size="tiny" /> : null}
                  onClick={onSubmitClick}
                  size="large"
                  appearance="primary">
                  Submit
                </Button>
              </div>
            </div>
            <div className={styles.articlesColumn}>
              <PopularArticles className={styles.articlesCard} />
            </div>
          </div>
        </div>
        <ConfirmationDialog
          className={styles.confirmationDialog}
          title="Ticket Submission Confirmation"
          text="Do you want to send this support request?"
          open={dialogOpen}
          onDeclineClick={() => setDialogOpen(false)}
          onConfirmClick={onConfirmClick}
        />
      </Card>
    </div>
  );
};
